/**
 * PaymentReferences — the payment's execution and artifact references, shown
 * as two tabs inside a container. Within each tab the rows are grouped by
 * direction (Outbound, Inbound), one embedded table per direction. Rows map
 * straight from the GetPaymentDetail executionRefs / artifactRefs lists.
 * Redux-connected; renders nothing when there are neither.
 */
import Box from '@cloudscape-design/components/box';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table, { TableProps } from '@cloudscape-design/components/table';
import Tabs from '@cloudscape-design/components/tabs';
import { useAppSelector } from 'src/store/hooks';
import { CopyableText } from 'src/components/common/CopyableText';
import { titleCase } from 'src/store/slices/paymentDetail/paymentDetailFormat';
import {
  ExecutionRefRow, ExecutionRefGroup, ArtifactRefRow, ArtifactRefGroup,
} from 'src/store/slices/paymentDetail/paymentDetailTypes';

const EXECUTION_COLUMNS: TableProps.ColumnDefinition<ExecutionRefRow>[] = [
  {
    id: 'executionId',
    header: 'Execution id',
    cell: row => (
      <CopyableText
        value={row.executionId}
        href={`/execution?id=${encodeURIComponent(row.executionId)}`}
      />
    ),
  },
  { id: 'executionType', header: 'Type', cell: row => row.executionType },
  { id: 'system', header: 'System', cell: row => row.system },
];

const ARTIFACT_COLUMNS: TableProps.ColumnDefinition<ArtifactRefRow>[] = [
  {
    id: 'artifactId',
    header: 'Artifact id',
    cell: row => (
      <CopyableText
        value={row.artifactId}
        href={`/artifact?id=${encodeURIComponent(row.artifactId)}`}
      />
    ),
  },
  { id: 'artifactType', header: 'Type', cell: row => row.artifactType },
];

/** Total rows across all direction groups. */
const total = (groups: { rows: unknown[] }[]): number => groups.reduce((n, group) => n + group.rows.length, 0);

const ExecutionGroups = ({ groups }: { groups: ExecutionRefGroup[] }) => (
  <SpaceBetween size="l">
    {groups.map(group => (
      <Table
        key={group.direction}
        variant="embedded"
        header={<Header variant="h3" counter={`(${group.rows.length})`}>{titleCase(group.direction)}</Header>}
        columnDefinitions={EXECUTION_COLUMNS}
        items={group.rows}
        trackBy="id"
        resizableColumns
      />
    ))}
  </SpaceBetween>
);

const ArtifactGroups = ({ groups }: { groups: ArtifactRefGroup[] }) => (
  <SpaceBetween size="l">
    {groups.map(group => (
      <Table
        key={group.direction}
        variant="embedded"
        header={<Header variant="h3" counter={`(${group.rows.length})`}>{titleCase(group.direction)}</Header>}
        columnDefinitions={ARTIFACT_COLUMNS}
        items={group.rows}
        trackBy="id"
        resizableColumns
      />
    ))}
  </SpaceBetween>
);

export const PaymentReferences = () => {
  const detail = useAppSelector(state => state.paymentDetail.data);
  if (!detail || (detail.executions.length === 0 && detail.artifacts.length === 0)) return null;

  return (
    <Tabs
      variant="container"
      tabs={[
        {
          id: 'executions',
          label: `Executions (${total(detail.executions)})`,
          content: detail.executions.length > 0
            ? <ExecutionGroups groups={detail.executions} />
            : <Box textAlign="center" padding="s">No executions</Box>,
        },
        {
          id: 'artifacts',
          label: `Artifacts (${total(detail.artifacts)})`,
          content: detail.artifacts.length > 0
            ? <ArtifactGroups groups={detail.artifacts} />
            : <Box textAlign="center" padding="s">No artifacts</Box>,
        },
      ]}
    />
  );
};
